/**
 * =========================================================================================
 * ATS Main component
 * =========================================================================================
 * Main routing (Organization) defined
 * Sub-routing defined
 *
 */

import React, {lazy, Suspense, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {Redirect, Route, Switch, useRouteMatch} from 'react-router-dom';
import * as Icon from "ubs-icons";
import UPermissionPopup from "ubs-framework/v2/UPermissionPopup";
import UVersionPopup from "ubs-framework/v2/UVersionPopup";
import Layout from "./common/layout";
import {Error403, Error404} from "./common/error-pages";
import ReleaseNotePopup from "./release-note/popup";
import NetworkDetector from "./common/HOCs/NetworkDetector";
import {sendData} from "../store/actions";
import FilePreview from "../common/components/file-preview";
import ViewReminder from "./reminder/components/view-reminder";
import DocumentUpload from "./public/document-upload";
import OfferAcceptDecline from "./public/offer-accept-decline";
import {PREBOARDING_STEPS, STATUS_CONST} from "../common/constant";
import usePlan from '../app-ats/common/hooks/usePlan';
import {PLAN} from 'ubs-framework/v2/common/constant';
import AddEvaluation from "./candidates/detail-view/components/common/evaluation/add-evaluation";
import CandidateEvaluationForm from "./candidates/detail-view/components/common/evaluation/candidate-evaluation";

const Dashboard = lazy(() => import('./dashboard'));
const Candidates = lazy(() => import('./candidates'));
const CandidateReminder = lazy(() => import('./reminder'));
const Interviews = lazy(() => import('./interviews'));
const Events = lazy(() => import('./events'));
const Jobs = lazy(() => import('./jobs'));
const Requisitions = lazy(() => import('./requisitions'));
const ATSReports = lazy(() => import('./reports'));
const EmployeeList = lazy(() => import('./employee-list'));
const Referral = lazy(() => import('./referral'));
const Settings = lazy(() => import('./settings'));
const UnassignedEmployees = lazy(() => import( "./settings/access-level/components/employees-list/unassigned-employees"));
const AllNotificationsList = lazy(() => import('./all-notifications-list'));
const Icons = lazy(() => import('./framework-component'));
const DownloadApp = lazy(() => import( "./download-app"));
const ReleaseNote = lazy(() => import( "./release-note"));
const Preboarding = lazy(() => import("./preboarding"));

const Organization = React.memo(() => {
    const __plan = usePlan();
    const {path, url} = useRouteMatch();

    const _PLAN = useSelector(e => e?.common?.planAccess);
    const _AS = useSelector(e => e?.ats?.uac?.settings?.modules);
    const _R = useSelector(e => e?.ats?.uac?.reminder?.modules?.reminder?.action?.read?.value);
    const _I = useSelector(e => e?.ats?.uac?.interview?.modules?.interview?.action?.read?.value);
    const _E = useSelector(e => e?.ats?.uac?.employee?.modules?.employee?.action?.read?.value);
    const _UAC_RPT = useSelector(e => e?.ats?.uac?.report?.modules?.report?.action?.read?.value);
    const _PBS = useSelector(e => e.common.preBoardingStatus);
    const _PST = useSelector(e => e.common.preBoardingStep);

    let _J_FLAGS = false;
    useSelector(e => e?.ats?.uac?.jobs?.modules ? Object?.values(e?.ats?.uac?.jobs?.modules).map(item => {
        if (Object.values(item?.action).find(action => action?.value)) _J_FLAGS = true
    }) : false)

    let _R_FLAGS = false;
    useSelector(e => e?.ats?.uac?.requisition?.modules ? Object?.values(e?.ats?.uac?.requisition?.modules).map(item => {
        if (Object.values(item?.action).find(action => action?.value)) _R_FLAGS = true
    }) : false)

    let _ASS_FLAG = true;
    useSelector(e => e?.ats?.uac?.settings?.modules ? Object?.values(e?.ats?.uac?.settings?.modules).map(item => {
        if (Object.values(item?.action).find(action => action?.value)) _ASS_FLAG = true
    }) : false)

    return (
        <React.Fragment>
            {/** Lazy Loading sub router setup using code splitting functionality */}
            <div className="u_ats u_relative u_ats-app-view">
                <Suspense fallback={<div className="u_preloader"><Icon.Preloader/></div>}>
                    <Switch>
                        <Route exact path={`${path}`}><Redirect to={`${url}/dashboard`}/></Route>
                        <Route path={`${path}/dashboard`} component={true ? Dashboard : Error404}/>

                        {/** Candidates **/}
                        <Route path={`${path}/candidates`} component={_J_FLAGS ? Candidates : Error404}/>
                        {/** Candidates Reminder **/}
                        <Route path={`${path}/reminder`} component={(__plan(PLAN.reminder) && _R) ? CandidateReminder : Error404}/>

                        {/** Interview **/}
                        <Route path={`${path}/interviews`} component={(__plan(PLAN.interview) && _I) ? Interviews : Error404}/>

                        {/** Events **/}
                        <Route path={`${path}/events`} component={true ? Events : Error404}/>

                        {/** Jobs **/}
                        <Route path={`${path}/jobs`} component={_J_FLAGS ? Jobs : Error404}/>

                        {/** Requisitions **/}
                        <Route path={`${path}/requisitions`} component={(__plan(PLAN.requisitions) && _R_FLAGS) ? Requisitions : Error404}/>

                        {/** Report **/}
                        <Route path={`${path}/reports`} component={ _UAC_RPT ? ATSReports : Error404 }/>

                        {/** EmployeeList **/}
                        <Route path={`${path}/employee-list`} component={(!_PLAN?.hrms?.default?.access && _E) ? EmployeeList : Error404}/>

                        {/** Referrals **/}
                        <Route path={`${path}/referral`} component={__plan(PLAN.referrals) ? Referral : Error404}/>

                        {/** Setting **/}
                        <Route path={`${path}/settings`} component={_ASS_FLAG ? Settings : Error404}/>

                        {/** Preboarding **/}
                        <Route path={`${path}/preboarding`} component={__plan(PLAN?.preBoarding) ? Preboarding : Error403}/>
                        <Route path={`${path}/boarding-document/:id`} component={([STATUS_CONST.RELEASE_OFFER, STATUS_CONST.OFFER_DECLINED, STATUS_CONST.OFFER_ACCEPTED, STATUS_CONST.READY_TO_ONBOARDING , STATUS_CONST.WAITING_FOR_CANDIDATE_APPROVAL]?.includes(_PBS) && _PST !== PREBOARDING_STEPS.RELEASE_OFFER) ? OfferAcceptDecline : DocumentUpload}/>

                        {/** Unassigned UAC Employees **/}
                        <Route exact path={`${path}/access-level/unassigned-employees`} component={_AS?.accessLevel?.action?.read?.value ? UnassignedEmployees : Error403}/>

                        {/** Notification **/}
                        <Route exact path={`${path}/notifications`} component={AllNotificationsList}/>

                        {/** Icons **/}
                        <Route exact path={`${path}/icons`} component={Icons}/>

                        {/** Download app **/}
                        <Route exact path={`${path}/downloads`} component={DownloadApp}/>

                        {/** Release Note **/}
                        <Route exact path={`${path}/release-note`} component={ReleaseNote}/>

                        <Route exact path="*" component={Error404}/>
                    </Switch>
                </Suspense>
            </div>
        </React.Fragment>
    );
});

const AppATS = () => {
    const __plan = usePlan();
    /**Pre Onboarding Document upload Path Redirection From account module*/
    let preBoardingPublic = useRouteMatch("/:orgId/boarding-document/:id")?.isExact;

    const _AEM = useSelector(e => e?.candidate?.addEvaluationModal || {});
    const _GEM = useSelector(e => e?.candidate?.giveEvaluationModal || {});
    const _R = useSelector(e => e.common.release);
    const _VERSION = useSelector(e => e.common.version);
    const _UAC_ID = useSelector(e => e.ats.uacID);
    const _SHOW_VERSION = useSelector(e => e.ats.isDisplayReleaseNotification);
    const _FILES = useSelector(e => e.common.files);
    const _UAC_UPDATE = useSelector(e => e.common?.uacUpdate);
    const _S_R = useSelector(e => !!e?.reminder?.showReminder);

    const [version, setVersion] = useState(0);

    useEffect(() => {
        if(!window?.location?.pathname?.includes("/boarding-document/")){
            sendData('getWorkspaceList', {});
            sendData('readProductVideo', {});
            sendData('getLatestVersion', {});
        }
    }, []);

    useEffect(() => {
        if (version === 0) setVersion(_VERSION);
    }, [_VERSION]);

    return (
        <React.Fragment>
            <Layout>
                {/** Router setup using nesting route functionality */}
                <Suspense fallback={<div>Loading...</div>}>
                    <Switch>
                        <Route path="/:orgId" component={Organization}/>
                    </Switch>
                </Suspense>
                {(_SHOW_VERSION && version !== 0 && _VERSION !== version && !preBoardingPublic )&&
                <UVersionPopup
                    close={() => setVersion(_VERSION)}
                    moduleIcon={<Icon.ATSLogo size={32}/>}
                    moduleName="Super Recruit"
                    refresh={() => window.location.reload()}/>
                }
                {((_UAC_UPDATE?._id === _UAC_ID || _UAC_UPDATE?.force ) && !preBoardingPublic) &&
                <UPermissionPopup
                    moduleIcon={<Icon.ATSLogo size={32}/>}
                    moduleName="Super Recruit"
                    refresh={() => window.location.reload()}/>}
                {(_R?.displayReleaseNotification && _R?.release?._id && !preBoardingPublic ) && <ReleaseNotePopup/>}
                {(_FILES && _FILES.files) && <FilePreview/>}
                {(__plan(PLAN.reminder) && _S_R && !preBoardingPublic)  && <ViewReminder/>}
                {_AEM?.flag && <AddEvaluation/>}
                {!!_GEM?.candidate && <CandidateEvaluationForm/>}
            </Layout>
        </React.Fragment>
    );
};

export default NetworkDetector(AppATS);
